import React, { useState } from 'react';
import './admission.css';
import Navbar from '../../Navbar/Navbar.jsx';
import Footer from '../../Footer/Footer.jsx';
import { Link } from 'react-router-dom';

/* ── data ──────────────────────────────────────────── */
const STEPS = [
  {
    num: '01',
    icon: '🏫',
    title: 'Visit or Enquire',
    desc: 'Schedule a campus visit or fill out the enquiry form online. Our admissions team will get in touch within one working day.',
    tag: 'Step One',
  },
  {
    num: '02',
    icon: '📋',
    title: 'Submit Application',
    desc: 'Complete the admission form and submit required documents. Forms are available online and at the school office.',
    tag: 'Step Two',
  },
  {
    num: '03',
    icon: '🤝',
    title: 'Interaction Session',
    desc: 'Attend a brief interaction with our academic team. This helps us understand your child\'s strengths and learning needs.',
    tag: 'Step Three',
  },
  {
    num: '04',
    icon: '✅',
    title: 'Confirmation',
    desc: 'Receive your admission confirmation letter and complete the fee payment to secure your child\'s seat.',
    tag: 'Step Four',
  },
];

const CLASSES = [
  { label: 'Nursery & LKG',     age: '3 – 4 years',   seats: 60, status: 'Open'   },
  { label: 'Class I – II',      age: '5 – 7 years',   seats: 120, status: 'Open'   },
  { label: 'Class III – V',     age: '8 – 10 years',  seats: 150, status: 'Open'   },
  { label: 'Class VI – VIII',   age: '11 – 13 years', seats: 150, status: 'Open'   },
  { label: 'Class IX – X',      age: '14 – 15 years', seats: 150, status: 'Limited'},
  ,
];

const DOCS = [
  { icon: '📄', label: 'Birth Certificate',           note: 'Original + photocopy' },
  { icon: '🏠', label: 'Address Proof',               note: 'Aadhaar / Passport' },
  { icon: '🏠', label: 'Address Proof of parents',    note: 'Aadhaar / Votecard' },
  { icon: '📸', label: 'StampSize Photos of child',   note: '4 recent photos' },
  { icon: '📸', label: 'Passport Size Photos of Parents',  note: '1 recent photos' },
  { icon: '📸', label: 'PostCard Size Photo of Family',    note: '1 recent photos' },
  { icon: '📋', label: 'Previous School TC',          note: 'For Class II onwards' },
  { icon: '📊', label: 'Last Year\'s Report Card',   note: 'For Class II onwards' },
  { icon: '💉', label: 'Blood Group Record',          note: 'Bloodgrouping card' },
];

const FAQS = [
  {
    q: 'When does the admission process begin each year?',
    a: 'Admissions open in Saraswati puja for the upcoming academic year (April–March). We recommend applying early as seats fill quickly, especially for Classes XI-X.',
  },
  {
    q: 'Is there an entrance test for admission?',
    a: 'For Classes I–VIII, admission is based on an informal interaction. For Classes IX–X, a written assessment in core subjects may be conducted.',
  },
  {
    q: 'Does Little Flowers School offer scholarships?',
    a: 'Yes. Merit scholarships are awarded to academically outstanding students. Need-based fee concessions are also available — contact our office for details.',
  },
  {
    q: 'What curriculum does the school follow?',
    a: 'LFS  is affiliated to the Central Board of Secondary Education (CBSE), Affiliation No. 153047, following the national curriculum framework.',
  },
  {
    q: 'Is hostel facility available?',
    a: 'Yes we do have an on-campus hostel. However, we can also assist families in identifying accommodation options near the school.',
  },
];

const FEE_HEADS = [
  { label: 'Admission Fee (one-time)', primary: '₹ 6k-7k', secondary: '₹ 8k-10k' },
  { label: 'Annual Fee',              primary: '₹ 12k-17k', secondary: '₹ 18k-20k' },
  { label: 'Monthly Tuition Fee',     primary: '₹ 1k-1.5k',  secondary: '₹ 1.5-2k'  },
  { label: 'Activity & Lab Charges',  primary: '₹ 0',  secondary: '₹ 0'  },
];

const Admission = () => {
  const [openFaq, setOpenFaq]   = useState(null);
  const [activeTab, setActiveTab] = useState('process');

  const [form, setForm] = useState({
    parentName: '', studentName: '', dob: '', applyClass: '',
    phone: '', email: '', prevSchool: '', message: '',
  });
  const [errors, setErrors]       = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending]     = useState(false);

  const validate = () => {
    const e = {};
    if (!form.parentName.trim())   e.parentName   = 'Parent name is required.';
    if (!form.studentName.trim())  e.studentName  = 'Student name is required.';
    if (!form.dob)                 e.dob          = 'Date of birth is required.';
    if (!form.applyClass)          e.applyClass   = 'Please select a class.';
    if (!form.phone.trim())        e.phone        = 'Phone number is required.';
    if (!form.email.trim())        e.email        = 'Email is required.';
    else if (!/\S+@\S+\.\S+/.test(form.email)) e.email = 'Enter a valid email.';
    return e;
  };

  const [submitError, setSubmitError] = useState('');

  const ADMISSION_SUBMIT_API_URL = 'https://lfsofficial.in/api/admin_admission_submit.php';

  const handleSubmit = async () => {
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    setErrors({});
    setSubmitError('');
    setSending(true);

    try {
      const response = await fetch(ADMISSION_SUBMIT_API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await response.json();

      if (data.success) {
        setSubmitted(true);
      } else {
        setSubmitError(data.message || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      setSubmitError(`Unable to reach the server: ${err.message}`);
    } finally {
      setSending(false);
    }
  };

  const F = (field) => ({
    value: form[field],
    onChange: (ev) => {
      setForm(f => ({ ...f, [field]: ev.target.value }));
      if (errors[field]) setErrors(er => ({ ...er, [field]: '' }));
    },
  });

  return (
    <div className="adm-page">
      <Navbar />

      {/* ══ HERO ══════════════════════════════════════════════ */}
      <section className="adm-hero">
        <div className="adm-hero__left">
          <span className="adm-eyebrow">2026 – 27 Admissions Open</span>
          <h1>
            Begin Your Child's<br />
            <em>Journey</em> Here
          </h1>
          <p>
            Join a community where ancient wisdom meets modern excellence.
            Little Flowers' School, Odisha — CBSE affiliated, spiritually enriched,
            academically outstanding.
          </p>
          <div className="adm-hero__ctas">
         <Link to="/contact" className="adm-btn adm-btn--white"> Apply Now</Link>
            <a href="#process" className="adm-btn adm-btn--ghost">How It Works</a>
          </div>
          <div className="adm-hero__chips">
            <span className="adm-chip">📚 CBSE Affiliated</span>
            <span className="adm-chip">🏆 25+ Years of Excellence</span>
            <span className="adm-chip">🌱 Hi-tech Education</span>
          </div>
        </div>

        <div className="adm-hero__right">
          <div className="adm-hero__card-stack">
            {/* Main card */}
            <div className="adm-hero__main-card">
              <img
                src="https://images.unsplash.com/photo-1580582932707-520aed937b7b?w=600&q=80"
                alt="Lfs Campus"
              />
              <div className="adm-hero__card-label">
                <span className="adm-card-dot" />
                Admissions Open
              </div>
            </div>
            {/* Floating stat cards */}
            <div className="adm-float-card adm-float-card--tl">
              <span className="adm-float-card__num">5,000+</span>
              <span className="adm-float-card__label">Alumni</span>
            </div>
            <div className="adm-float-card adm-float-card--br">
              <span className="adm-float-card__num">30+</span>
              <span className="adm-float-card__label">Faculty</span>
            </div>
          </div>
        </div>

        {/* Background rings */}
        <div className="adm-hero__rings">
          {[...Array(4)].map((_, i) => (
            <div className="adm-ring" key={i} style={{ '--i': i }} />
          ))}
        </div>
      </section>

      {/* ══ STATS BAND ══════════════════════════════════════════ */}
      <div className="adm-stats-band">
        {[
          { val: '25+',  label: 'Years of Excellence' },
          { val: 'CBSE', label: 'Affiliation No.  153047' },
          { val: '5000+',label: 'Students Enrolled' },
          { val: '30+', label: 'Qualified Faculty' },
          { val: '50+',  label: 'Awards & Accolades' },
          { val: '5T', label: 'Classes Offered' },
        ].map((s, i) => (
          <div className="adm-stat" key={i}>
            <strong>{s.val}</strong>
            <span>{s.label}</span>
          </div>
        ))}
      </div>

      {/* ══ TABS NAV ══════════════════════════════════════════ */}
      <div className="adm-tabs-nav" id="process">
        {[
          { key: 'process',   label: '📋 Process'    },
          { key: 'seats',     label: '🏫 Available Seats' },
          { key: 'documents', label: '📁 Documents'  },
          { key: 'fees',      label: '💰 Fee Structure' },
        ].map(t => (
          <button
            key={t.key}
            className={`adm-tab-btn ${activeTab === t.key ? 'adm-tab-btn--active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ══ TAB CONTENT ═════════════════════════════════════ */}
      <section className="adm-tab-content">

        {/* PROCESS */}
        {activeTab === 'process' && (
          <div className="adm-process">
            <div className="adm-section-head">
              <h2>Admission Process</h2>
              <p>Four simple steps to join the LFS family.</p>
            </div>
            <div className="adm-steps">
              {STEPS.map((step, i) => (
                <div className="adm-step" key={i}>
                  <div className="adm-step__left">
                    <div className="adm-step__num">{step.num}</div>
                    {i < STEPS.length - 1 && <div className="adm-step__line" />}
                  </div>
                  <div className="adm-step__body">
                    <span className="adm-step__tag">{step.tag}</span>
                    <div className="adm-step__card">
                      <span className="adm-step__icon">{step.icon}</span>
                      <h3>{step.title}</h3>
                      <p>{step.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* SEATS */}
        {activeTab === 'seats' && (
          <div className="adm-seats">
            <div className="adm-section-head">
              <h2>Available Seats 2026–27</h2>
              <p>Seat availability is updated regularly. Limited seats are filling fast.</p>
            </div>
            <div className="adm-seats__grid">
              {CLASSES.map((cls, i) => (
                <div className={`adm-seat-card adm-seat-card--${cls.status.toLowerCase()}`} key={i}>
                  <div className="adm-seat-card__top">
                    <h3>{cls.label}</h3>
                    <span className={`adm-status adm-status--${cls.status.toLowerCase()}`}>
                      {cls.status}
                    </span>
                  </div>
                  <div className="adm-seat-card__meta">
                    <div className="adm-seat-meta-row">
                      <span>Age Group</span>
                      <strong>{cls.age}</strong>
                    </div>
                    <div className="adm-seat-meta-row">
                      <span>Total Seats</span>
                      <strong>{cls.seats}</strong>
                    </div>
                  </div>
                  <a href="#apply-form" className="adm-seat-card__btn">Apply →</a>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* DOCUMENTS */}
        {activeTab === 'documents' && (
          <div className="adm-docs">
            <div className="adm-section-head">
              <h2>Required Documents</h2>
              <p>Please bring original documents along with one set of photocopies.</p>
            </div>
            <div className="adm-docs__grid">
              {DOCS.map((doc, i) => (
                <div className="adm-doc-card" key={i}>
                  <span className="adm-doc-card__icon">{doc.icon}</span>
                  <div>
                    <strong>{doc.label}</strong>
                    <span>{doc.note}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="adm-docs__note">
              <span>💡</span>
              <p>
                All documents must be self-attested. Originals will be returned after
                verification. Incomplete applications will not be processed.
              </p>
            </div>
          </div>
        )}

        {/* FEES */}
        {activeTab === 'fees' && (
          <div className="adm-fees">
            <div className="adm-section-head">
              <h2>Fee Structure 2026–27</h2>
              <p>Indicative fee details. Final amounts are communicated at the time of admission.</p>
            </div>
            <div className="adm-fees__table-wrap">
              <table className="adm-fees__table">
                <thead>
                  <tr>
                    <th>Fee Head</th>
                    <th>Primary (I–V)</th>
                    <th>Secondary (VI–VII)</th>
                  </tr>
                </thead>
                <tbody>
                  {FEE_HEADS.map((row, i) => (
                    <tr key={i}>
                      <td>{row.label}</td>
                      <td><strong>{row.primary}</strong></td>
                      <td><strong>{row.secondary}</strong></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="adm-fees__note">
              <span>📌</span>
              <p>
                Fees are subject to revision. Scholarships and concessions are
                available for meritorious and economically weaker students.
                Contact the accounts office for details.
              </p>
            </div>
          </div>
        )}

      </section>

      {/* ══ WHY LFS ════════════════ */}
      <section className="adm-why">
        <div className="adm-why__left">
          <span className="adm-section-eyebrow">Why Choose Us</span>
          <h2>An Education That Shapes the Whole Child</h2>
          <p>
            At Little Flowers School we believe every child carries limitless potential.
            Our approach combines rigorous academics with spiritual values, creative
            expression, and athletic development — producing graduates who are
            confident, compassionate, and ready for life.
          </p>
          <ul className="adm-why__list">
            {[
              'Experienced & dedicated faculty',
              'State-of-the-art science & computer labs',
              'Rich co-curricular & sports programmes',
              'Safe, spiritually nurturing environment',
              'CBSE curriculum with strong foundations',
              'Active parent-school partnership',
            ].map((item, i) => (
              <li key={i}>
                <span className="adm-check">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>
        <div className="adm-why__right">
          <div className="adm-why__img-grid">
            <img
              src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&q=80"
              alt="Students studying"
              className="adm-why__img adm-why__img--tall"
            />
            <img
              src="https://images.unsplash.com/photo-1509062522246-3755977927d7?w=400&q=80"
              alt="Classroom"
            />
            <img
              src="https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=400&q=80"
              alt="School activities"
            />
          </div>
        </div>
      </section>

      {/* ══ APPLICATION FORM ═══════════════════════════════ */}
      <section className="adm-form-section" id="apply-form">
        <div className="adm-form-section__inner">
          <div className="adm-form-header">
            <span className="adm-section-eyebrow">Apply Now</span>
            <h2>Start Your Application</h2>
            <p>
              Fill in the details below and our admissions team will
              contact you within Three working day.
            </p>
          </div>

          {submitted ? (
            <div className="adm-success">
              <div className="adm-success__icon">🎉</div>
              <h3>Application Received!</h3>
              <p>
                Thank you for applying to LFS. Our admissions team
                will contact you at <strong>{form.email || 'your email'}</strong> within one working day.
              </p>
              <button className="adm-btn adm-btn--primary" onClick={() => { setSubmitted(false); setForm({ parentName: '', studentName: '', dob: '', applyClass: '', phone: '', email: '', prevSchool: '', message: '' }); }}>
                Submit Another Application
              </button>
            </div>
          ) : (
            <div className="adm-form">
              <div className="adm-form__section-label">Parent / Guardian Details</div>
              <div className="adm-form__grid">
                <div className={`adm-field ${errors.parentName ? 'adm-field--err' : ''}`}>
                  <label>Parent / Guardian Name *</label>
                  <input type="text" placeholder="e.g. Ramesh Kumar" {...F('parentName')} />
                  {errors.parentName && <span>{errors.parentName}</span>}
                </div>
                <div className={`adm-field ${errors.phone ? 'adm-field--err' : ''}`}>
                  <label>Mobile Number *</label>
                  <input type="tel" placeholder="+91 98765 43210" {...F('phone')} />
                  {errors.phone && <span>{errors.phone}</span>}
                </div>
                <div className={`adm-field adm-field--full ${errors.email ? 'adm-field--err' : ''}`}>
                  <label>Email Address *</label>
                  <input type="email" placeholder="you@example.com" {...F('email')} />
                  {errors.email && <span>{errors.email}</span>}
                </div>
              </div>

              <div className="adm-form__section-label">Student Details</div>
              <div className="adm-form__grid">
                <div className={`adm-field ${errors.studentName ? 'adm-field--err' : ''}`}>
                  <label>Student's Full Name *</label>
                  <input type="text" placeholder="e.g. Priya Kumar" {...F('studentName')} />
                  {errors.studentName && <span>{errors.studentName}</span>}
                </div>
                <div className={`adm-field ${errors.dob ? 'adm-field--err' : ''}`}>
                  <label>Date of Birth *</label>
                  <input type="date" {...F('dob')} />
                  {errors.dob && <span>{errors.dob}</span>}
                </div>
                <div className={`adm-field ${errors.applyClass ? 'adm-field--err' : ''}`}>
                  <label>Applying for Class *</label>
                  <select {...F('applyClass')}>
                    <option value="">Select Class</option>
                    <option>Nursery</option>
                    <option>LKG</option>
                    <option>UKG</option>
                    {[...Array(12)].map((_, i) => (
                      <option key={i}>Class {i + 1}</option>
                    ))}
                  </select>
                  {errors.applyClass && <span>{errors.applyClass}</span>}
                </div>
                <div className="adm-field">
                  <label>Previous School <small>(if applicable)</small></label>
                  <input type="text" placeholder="Name of previous school" {...F('prevSchool')} />
                </div>
                <div className="adm-field adm-field--full">
                  <label>Additional Message <small>(optional)</small></label>
                  <textarea placeholder="Any specific requirements or questions..." rows={4} {...F('message')} />
                </div>
              </div>

              <div className="adm-form__footer">
                <p className="adm-form__disclaimer">
                  By submitting this form you agree to be contacted by our admissions team.
                  Your information is kept strictly confidential.
                </p>
                {submitError && <div className="adm-field--err" style={{ marginBottom: 12 }}><span>{submitError}</span></div>}
                <button
                  className={`adm-btn adm-btn--primary adm-btn--large ${sending ? 'adm-btn--sending' : ''}`}
                  onClick={handleSubmit}
                  disabled={sending}
                >
                  {sending
                    ? <><span className="adm-spinner" /> Submitting...</>
                    : '🚀 Submit Application'
                  }
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ══ FAQ ═════════════════════ */}
      <section className="adm-faq">
        <div className="adm-faq__inner">
          <div className="adm-section-head">
            <span className="adm-section-eyebrow">Got Questions?</span>
            <h2>Frequently Asked Questions</h2>
          </div>
          <div className="adm-faq__list">
            {FAQS.map((faq, i) => (
              <div
                className={`adm-faq__item ${openFaq === i ? 'adm-faq__item--open' : ''}`}
                key={i}
              >
                <button
                  className="adm-faq__q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  <span>{faq.q}</span>
                  <span className="adm-faq__icon">{openFaq === i ? '−' : '+'}</span>
                </button>
                <div className="adm-faq__a">
                  <p>{faq.a}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ BOTTOM CTA ═══════════════════ */}
      <section className="adm-cta">
        <div className="adm-cta__rings">
          {[...Array(3)].map((_, i) => <div className="adm-cta__ring" key={i} style={{ '--i': i }} />)}
        </div>
        <div className="adm-cta__content">
          <h2>Still Have Questions?</h2>
          <p>Our admissions counsellors are happy to help you every step of the way.</p>
          <div className="adm-cta__actions">
            <Link to="/contact" className="adm-btn adm-btn--white"> contact us</Link>
            <a href="/Gallery" className="adm-btn adm-btn--ghost-light">🖼 View Gallery</a>
          </div>
          <div className="adm-cta__contact-line">
            <span>📞 7655022486</span>
            <span>·</span>
            <span>✉️ lfsrkrrpmbj@gmail.com</span>
            <span>·</span>
            <span>📍 Rairangpur PO, Mayurbhanj, Odisha, India – 757043</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Admission;